import React, { memo, useCallback, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { Box } from '@mui/material';
import AutocompleteCDS from '@sharedCR/coreDesignSystem/form/AutocompleteCDS';
import DataFieldCDS from '@sharedCR/coreDesignSystem/form/DataFieldCDS';
import DateFieldCDS from '@sharedCR/coreDesignSystem/form/DateFieldCDS';
import TextFieldCDS from '@sharedCR/coreDesignSystem/form/TextFieldCDS';
import dayjs from 'dayjs';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { number, object, string } from 'yup';

import { COMMON_REQUEST_DATE_FORMAT } from 'constants/date';
import { FieldInputType } from 'constants/fields';
import useForm from 'hooks/useForm';
import { logger } from 'utils/logger';

import {
  createServicingEscrowDisbursementAction,
  updateServicingEscrowDisbursementAction,
} from 'store/loanApplications/actions';

import { DISBURSEMENT_TYPE_OPTIONS } from '../../Servicing.constants';
import { buildDisbursementPayload } from '../../Servicing.utils';
import { EscrowActivityTestIds } from '../EscrowActivity.testIds';

const disbursementSchema = object().shape({
  disbursementDate: string().nullable().required('Disbursement date is required'),
  disbursementType: object().nullable().required('Disbursement type is required'),
  payeeName: string().required('Payee name is required'),
  amount: number().typeError('Amount must be a number').required('Amount is required'),
});

const OBSERVABLE_FIELDS = [
  'disbursementDate',
  'disbursementType',
  'payeeName',
  'amount',
  'referenceNumber',
  'notes',
];

const formFieldsStyles = {
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  padding: '24px',
};

const getInitialFormValues = disbursement => ({
  disbursementDate: disbursement?.disbursementDate ?? dayjs().format(COMMON_REQUEST_DATE_FORMAT),
  disbursementType: disbursement
    ? (DISBURSEMENT_TYPE_OPTIONS.find(option => option.value === disbursement.disbursementType) ??
      null)
    : null,
  payeeName: disbursement?.payeeName ?? '',
  amount: _.isNil(disbursement?.amount) ? '' : String(disbursement.amount),
  referenceNumber: disbursement?.referenceNumber ?? '',
  notes: disbursement?.notes ?? '',
});

const EscrowDisbursementDrawerContent = ({
  setIsFormChanged,
  isSaving,
  setIsLoading,
  additionalProps: { disbursement, applicationId, onSuccess },
  setOnSave,
}) => {
  const dispatch = useDispatch();
  const isEditing = !!disbursement;

  const initialFormValues = useMemo(() => getInitialFormValues(disbursement), [disbursement]);

  const {
    form,
    handleChange,
    isFormChanged,
    setInForm,
    setInitialFormValues,
    setForm,
    setSchema,
    validateForm,
    validationErrors,
  } = useForm(initialFormValues, {}, OBSERVABLE_FIELDS);

  useEffect(() => {
    setSchema(disbursementSchema);
  }, [setSchema]);

  useEffect(() => {
    setIsFormChanged(isFormChanged);
  }, [isFormChanged, setIsFormChanged]);

  useEffect(() => {
    setInitialFormValues(initialFormValues);
    setForm(initialFormValues);
  }, [initialFormValues, setInitialFormValues, setForm]);

  const onTypeChange = useCallback(
    (_, selectedOption) => {
      setInForm('disbursementType', selectedOption);
    },
    [setInForm],
  );

  const onDateChange = useCallback(
    value => {
      setInForm('disbursementDate', value);
    },
    [setInForm],
  );

  const onSave = useCallback(async () => {
    const isValid = await validateForm({ noFormChangedValidate: true });

    if (!isValid) {
      return;
    }

    setIsLoading(true);

    const body = buildDisbursementPayload(form);

    try {
      if (isEditing) {
        await dispatch(
          updateServicingEscrowDisbursementAction({
            applicationId,
            disbursementId: disbursement.id,
            ...body,
          }),
        );
      }

      if (!isEditing) {
        await dispatch(
          createServicingEscrowDisbursementAction({
            applicationId,
            ...body,
          }),
        );
      }

      onSuccess();
    } catch (e) {
      logger.error(e);
    } finally {
      setIsLoading(false);
    }
  }, [
    form,
    isEditing,
    dispatch,
    applicationId,
    disbursement,
    onSuccess,
    setIsLoading,
    validateForm,
  ]);

  useEffect(() => {
    setOnSave(() => onSave);
  }, [setOnSave, onSave]);

  return (
    <Box sx={formFieldsStyles}>
      <DateFieldCDS
        required
        dataTestId={EscrowActivityTestIds.DISBURSEMENT_DATE}
        error={!!validationErrors.disbursementDate}
        helperText={validationErrors.disbursementDate}
        label="Disbursement Date"
        name="disbursementDate"
        value={form.disbursementDate}
        onChange={onDateChange}
      />

      <AutocompleteCDS
        required
        dataTestId={EscrowActivityTestIds.TYPE_SELECT}
        error={!!validationErrors.disbursementType}
        helperText={validationErrors.disbursementType}
        label="Disbursement Type"
        name="disbursementType"
        optionKey="value"
        optionName="name"
        options={DISBURSEMENT_TYPE_OPTIONS}
        value={form.disbursementType}
        onChange={onTypeChange}
      />

      <TextFieldCDS
        required
        dataTestId={EscrowActivityTestIds.PAYEE_NAME_INPUT}
        disabled={isSaving}
        error={!!validationErrors.payeeName}
        helperText={validationErrors.payeeName}
        label="Payee Name"
        name="payeeName"
        value={form.payeeName}
        onChange={handleChange}
      />

      <DataFieldCDS
        required
        disabled={isSaving}
        error={!!validationErrors.amount}
        helperText={validationErrors.amount}
        label="Amount"
        name="amount"
        type={FieldInputType.CURRENCY}
        value={form.amount}
        onChange={handleChange}
      />

      <TextFieldCDS
        dataTestId={EscrowActivityTestIds.REFERENCE_NUMBER_INPUT}
        disabled={isSaving}
        label="Reference Number"
        name="referenceNumber"
        value={form.referenceNumber}
        onChange={handleChange}
      />

      <TextFieldCDS
        multiline
        dataTestId={EscrowActivityTestIds.NOTES_INPUT}
        disabled={isSaving}
        label="Notes"
        name="notes"
        rows={3}
        value={form.notes}
        onChange={handleChange}
      />
    </Box>
  );
};

EscrowDisbursementDrawerContent.propTypes = {
  additionalProps: PropTypes.shape({
    applicationId: PropTypes.string.isRequired,
    onSuccess: PropTypes.func.isRequired,
    disbursement: PropTypes.object,
  }).isRequired,
  setIsFormChanged: PropTypes.func.isRequired,
  setIsLoading: PropTypes.func.isRequired,
  setOnSave: PropTypes.func.isRequired,
  isSaving: PropTypes.bool,
};

export default memo(EscrowDisbursementDrawerContent);
